import { onAuthStateChanged, type User } from 'firebase/auth';
import { doc, onSnapshot, updateDoc } from 'firebase/firestore';
import { create } from 'zustand';
import { auth, db } from '@/lib/firebase';
import type { ContentType, ReaderTheme, ThemeMode, UserDoc } from '@/types';
import { applyContentType, applyTheme, useUiStore } from './uiStore';
import { useReaderStore } from './readerStore';

export type SessionStatus =
  /** Todavía no sabemos si hay sesión. */
  | 'loading'
  /** No hay sesión iniciada. */
  | 'anon'
  /** Hay sesión de Auth pero falta el perfil (handle + nombre). */
  | 'needs-onboarding'
  /** Sesión y perfil completos. */
  | 'ready';

interface SessionState {
  status: SessionStatus;
  user: User | null;
  profile: UserDoc | null;
  isAdmin: boolean;
  /** Corta los listeners activos. Lo usa el bootstrap al desmontar. */
  dispose: (() => void) | null;
  init: () => () => void;
}

/** Vuelca las preferencias del perfil sobre los stores de UI. */
function hydratePreferences(profile: UserDoc): void {
  const ui = useUiStore.getState();
  const reader = useReaderStore.getState();
  const prefs = profile.preferences;

  if (prefs.theme !== ui.theme) {
    applyTheme(prefs.theme as ThemeMode);
    useUiStore.setState({ theme: prefs.theme });
  }
  if (prefs.lastContentType !== ui.contentType) {
    applyContentType(prefs.lastContentType as ContentType);
    useUiStore.setState({ contentType: prefs.lastContentType });
  }
  if (prefs.readerTheme !== reader.readerTheme || prefs.fontSize !== reader.fontSize) {
    useReaderStore.setState({
      readerTheme: prefs.readerTheme as ReaderTheme,
      fontSize: prefs.fontSize,
    });
  }
}

export const useSessionStore = create<SessionState>((set) => ({
  status: 'loading',
  user: null,
  profile: null,
  isAdmin: false,
  dispose: null,

  init: () => {
    let unsubProfile: (() => void) | null = null;

    const unsubAuth = onAuthStateChanged(auth, async (user) => {
      unsubProfile?.();
      unsubProfile = null;

      if (!user) {
        set({ status: 'anon', user: null, profile: null, isAdmin: false });
        return;
      }

      const token = await user.getIdTokenResult();
      set({ user, isAdmin: token.claims.admin === true });

      unsubProfile = onSnapshot(
        doc(db, 'users', user.uid),
        (snap) => {
          if (!snap.exists()) {
            set({ status: 'needs-onboarding', profile: null });
            return;
          }
          const profile = snap.data() as UserDoc;
          hydratePreferences(profile);
          set({ status: 'ready', profile });
        },
        () => set({ status: 'needs-onboarding', profile: null }),
      );
    });

    const dispose = () => {
      unsubProfile?.();
      unsubAuth();
    };
    set({ dispose });
    return dispose;
  },
}));

/**
 * Persiste una preferencia en el perfil. Para usuarios anónimos no hace nada:
 * ahí manda localStorage vía los stores persistidos.
 */
export async function persistPreference(
  patch: Partial<UserDoc['preferences']>,
): Promise<void> {
  const { user, status } = useSessionStore.getState();
  if (!user || status !== 'ready') return;
  const entries = Object.entries(patch).map(([key, value]) => [`preferences.${key}`, value]);
  await updateDoc(doc(db, 'users', user.uid), Object.fromEntries(entries));
}

/** Atajos de lectura, para no repetir selectores en los componentes. */
export const useCurrentUser = () => useSessionStore((s) => s.user);
export const useProfile = () => useSessionStore((s) => s.profile);
export const useIsAdmin = () => useSessionStore((s) => s.isAdmin);
export const useSessionStatus = () => useSessionStore((s) => s.status);
